// @givmo/compliance — the send review's left column: the package and the email, exactly as they go
// (CCM-R1-SUBMIT-WEB; slice 3 §5.2–§5.3, frames 62 and 72).
//
// Both are drawn from the server's read, never re-composed: each file as the package carries it
// (its title, its role, the name it travels under, its pages, size and SHA-256), and the email's
// subject and body as served. Once a send is made, what went is drawn from the authorization that
// bound it and the addresses the send recorded, so the column keeps showing what went even when
// the case changes after.

import type { ReactNode } from 'react';
import { Card, Icon, testIdProp } from '@givmo/design-system';
import { FileText, Paperclip } from 'lucide-react';
import { displayArtifactSize } from './compliance-decode';
import { ComplianceDisclosure } from './compliance-disclosure';
import { instructionLine } from './compliance-preparation-model';
import { moreNotNeededLine } from './compliance-signing-model';
import type { PackageRole } from './compliance-submission-decode';
import {
  EMAIL_HEADER_LABELS,
  EMAIL_LEAD,
  OPEN,
  SIGNED_FORM_ROLE,
  THE_EMAIL,
  THE_PACKAGE,
  checklistRole,
  filesTotalLine,
  inPlaceOf,
  packageLead,
  pagesWords,
  shortDigest,
} from './compliance-submission-model';
import { QuietButton } from './filing-workbench-pages';

const PREFIX = 'compliance-send';

/** One file as the page lists it: the package's entry, or the bound entry of what went. */
export interface ShownFile {
  key: string;
  documentId: string;
  versionId: string;
  /** The form's name for the signed form; an attachment's title as the organization recorded it. */
  title: string;
  role: PackageRole | 'unknown';
  checklistItem?: string;
  fileName: string;
  /** Unknown for a bound file the package no longer lists (the authorization binds no pages). */
  pageCount?: number;
  byteSize: number;
  contentSha256: string;
  /** The agency's own words the checklist item carries (D-CCM-24 J3), quoted under the file. */
  agencyQuote?: { text: string };
}

/** The section idiom of the panel and the workbench: a 14px semibold title over its body. */
export function SendSection({
  title,
  lead,
  children,
  testId,
}: {
  title: string;
  lead?: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <section className="flex flex-col gap-10" {...testIdProp(testId)}>
      <h2 className="text-s14 font-w600 text-text-strong">{title}</h2>
      {lead !== undefined && <p className="-mt-4 text-s13 text-text-secondary">{lead}</p>}
      {children}
    </section>
  );
}

/** The quiet line under a value: where it came from, or who entered it. */
export function SourceLine({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <p className="text-s12 leading-h15 text-text-tertiary" {...testIdProp(testId)}>
      {children}
    </p>
  );
}

function roleLine(file: ShownFile): string {
  if (file.role === 'signed_form') return SIGNED_FORM_ROLE;
  return file.checklistItem !== undefined ? checklistRole(file.checklistItem) : '';
}

/** "{file} · {n} pages · {size} · SHA-256 {4}…{4}", in frame 46's digest idiom. */
function FileFacts({ file }: { file: ShownFile }) {
  const size = displayArtifactSize(file.byteSize);
  return (
    <p className="text-s12 leading-h15 text-text-tertiary">
      <span className="text-text-secondary">{file.fileName}</span>
      {file.pageCount !== undefined && (
        <>
          {' · '}
          <span className="whitespace-nowrap">{pagesWords(file.pageCount)}</span>
        </>
      )}
      {size.length > 0 && (
        <>
          {' · '}
          <span className="whitespace-nowrap">{size}</span>
        </>
      )}
      {' · '}
      <span className="whitespace-nowrap tabular-nums">
        SHA-256 {shortDigest(file.contentSha256)}
      </span>
    </p>
  );
}

/** The package's files, one row each in the package's order, each with Open (a logged read). */
export function PackageList({
  files,
  jurisdiction,
  onOpen,
}: {
  files: readonly ShownFile[];
  jurisdiction: string;
  onOpen: (file: ShownFile) => void;
}) {
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-border-subtle">
        {files.map((file) => {
          const role = roleLine(file);
          return (
            <li
              key={file.key}
              className="flex items-start gap-12 px-20 py-14"
              {...testIdProp(`${PREFIX}-package-row`)}
            >
              <Icon
                icon={FileText}
                size={16}
                className="mt-2 shrink-0 text-icon-muted"
                aria-hidden
              />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="flex items-baseline justify-between gap-12">
                  <p className="min-w-0 text-s14 leading-h15 text-text">{file.title}</p>
                  <QuietButton onClick={() => onOpen(file)} testId={`${PREFIX}-package-open`}>
                    {OPEN}
                  </QuietButton>
                </div>
                {role.length > 0 && <SourceLine>{role}</SourceLine>}
                <FileFacts file={file} />
                {file.agencyQuote !== undefined && (
                  <SourceLine testId={`${PREFIX}-package-quote`}>
                    {instructionLine(jurisdiction, file.agencyQuote.text)}
                  </SourceLine>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/**
 * "The package": one row per file in the package's order, each with Open (the exact bytes, a
 * logged read), then the not-needed count, the total, and — on the review only — row 7's sentence.
 * `notNeeded` is undefined where it isn't known (an authorization made before Givmo kept its
 * checklist): then no count line is drawn (CCM-R1-SUBMIT-SENT-SUMMARY).
 */
export function SendPackageSection({
  files,
  jurisdiction,
  notNeeded,
  totalBytes,
  sentence,
  onOpen,
}: {
  files: readonly ShownFile[];
  jurisdiction: string;
  notNeeded: number | undefined;
  totalBytes: number;
  /** Row 7's sentence, under the package until sending starts (O4 (A)). */
  sentence: boolean;
  onOpen: (file: ShownFile) => void;
}) {
  const more = notNeeded !== undefined ? moreNotNeededLine(notNeeded) : undefined;
  return (
    <SendSection
      title={THE_PACKAGE}
      lead={packageLead(files.length, jurisdiction)}
      testId={`${PREFIX}-package`}
    >
      <PackageList files={files} jurisdiction={jurisdiction} onOpen={onOpen} />
      <div className="flex flex-col gap-4">
        {more !== undefined && (
          <p
            className="text-s12 leading-h15 text-text-tertiary"
            {...testIdProp(`${PREFIX}-not-needed`)}
          >
            {more}
          </p>
        )}
        <p className="text-s12 leading-h15 text-text-tertiary" {...testIdProp(`${PREFIX}-total`)}>
          {filesTotalLine(files.length, totalBytes)}
        </p>
      </div>
      {sentence && <ComplianceDisclosure surface="send-review" />}
    </SendSection>
  );
}

/** The email as this environment sends it: its header, its subject, its body, its attachments. */
export interface ShownEmail {
  from: string;
  to: readonly string[];
  cc: readonly string[];
  replyTo?: string;
  /** Outside production: the agency's address the test address stands in for (frame 72). */
  inPlaceOf?: string;
  subject: string;
  body: string;
  attachments: readonly { key: string; fileName: string; byteSize: number }[];
}

/**
 * "The email", drawn as a mail client draws it (the J-e notices' idiom): a grey header band, the
 * body on white in plain text, then the files as attachment chips.
 */
export function SendEmailSection({ email }: { email: ShownEmail }) {
  const header: [string, ReactNode][] = [
    [EMAIL_HEADER_LABELS.from, email.from],
    [
      EMAIL_HEADER_LABELS.to,
      email.inPlaceOf !== undefined ? (
        <>
          {email.to.join(', ')}{' '}
          <span className="text-text-tertiary">· {inPlaceOf(email.inPlaceOf)}</span>
        </>
      ) : (
        email.to.join(', ')
      ),
    ],
    ...(email.cc.length > 0
      ? ([[EMAIL_HEADER_LABELS.cc, email.cc.join(', ')]] as [string, ReactNode][])
      : []),
    ...(email.replyTo !== undefined
      ? ([[EMAIL_HEADER_LABELS.replyTo, email.replyTo]] as [string, ReactNode][])
      : []),
  ];
  return (
    <SendSection title={THE_EMAIL} lead={EMAIL_LEAD} testId={`${PREFIX}-email`}>
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-12 border-b border-border-subtle bg-surface-sunken px-20 py-16">
          <dl className="grid grid-cols-[72px_minmax(0,1fr)] gap-x-12 gap-y-4 text-s13">
            {header.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-text-tertiary">{label}</dt>
                <dd
                  className="min-w-0 break-words text-text"
                  {...testIdProp(`${PREFIX}-email-${label.toLowerCase()}`)}
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <p
            className="text-s16 font-w600 leading-h15 text-text-strong"
            {...testIdProp(`${PREFIX}-email-subject`)}
          >
            {email.subject}
          </p>
        </div>
        <p
          className="whitespace-pre-wrap break-words px-20 py-16 text-s14 leading-h15 text-text"
          {...testIdProp(`${PREFIX}-email-body`)}
        >
          {email.body}
        </p>
        {email.attachments.length > 0 && (
          <div className="flex flex-wrap gap-8 border-t border-border-subtle px-20 py-12">
            {email.attachments.map((file) => (
              <span
                key={file.key}
                className="inline-flex max-w-full items-center gap-6 rounded-md border border-border-subtle px-8 py-4 text-s12 text-text"
              >
                <Icon icon={Paperclip} size={13} className="shrink-0 text-icon-muted" aria-hidden />
                <span className="min-w-0 truncate">{file.fileName}</span>
                <span className="shrink-0 text-text-tertiary">
                  {displayArtifactSize(file.byteSize)}
                </span>
              </span>
            ))}
          </div>
        )}
      </Card>
    </SendSection>
  );
}
